import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutDashboard, Users, KanbanSquare, CalendarClock, Contact, Handshake, BarChart3, Plus, ClipboardList, Upload, Facebook, Bell, Settings2, Menu, X, Lock, Loader2, CheckCircle2, ExternalLink, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import CrmDashboard from "../../components/crm/CrmDashboard";
import CrmLeads from "../../components/crm/CrmLeads";
import CrmPipeline from "../../components/crm/CrmPipeline";
import CrmFollowUps from "../../components/crm/CrmFollowUps";
import CrmContacts from "../../components/crm/CrmContacts";
import CrmDeals from "../../components/crm/CrmDeals";
import CrmReports from "../../components/crm/CrmReports";
import CrmForms from "../../components/crm/CrmForms";
import CrmImport from "../../components/crm/CrmImport";
import CrmFacebook from "../../components/crm/CrmFacebook";
import CrmPipelines from "../../components/crm/CrmPipelines";
import CrmNotifications from "../../components/crm/CrmNotifications";
import CrmSettings from "../../components/crm/CrmSettings";
import { LeadModal } from "../../components/crm/CrmShared";

const SECTIONS = [
  { k: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { k: "leads", label: "Leads", icon: Users },
  { k: "contacts", label: "Contacts", icon: Contact },
  { k: "pipeline", label: "Sales Pipeline", icon: KanbanSquare },
  { k: "deals", label: "Deals", icon: Handshake },
  { k: "followups", label: "Follow-ups", icon: CalendarClock },
  { k: "forms", label: "Forms", icon: ClipboardList },
  { k: "import", label: "Import Leads", icon: Upload },
  { k: "facebook", label: "Facebook Leads", icon: Facebook },
  { k: "reports", label: "Reports", icon: BarChart3 },
  { k: "notifications", label: "Notifications", icon: Bell },
  { k: "settings", label: "CRM Settings", icon: Settings2 },
];

const CRM_FEATURES = ["Lead Management", "Sales Pipeline", "Custom Forms", "Follow-ups", "Deals", "Reports", "CRM Analytics", "Facebook Lead Integration where configured"];

function CrmPayPanel({ ent, onActivated }) {
  const [form, setForm] = useState({ reference: "", paid_at: "", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/store-admin/crm-billing/submit", form);
      toast.success("Your CRM payment has been submitted for verification.");
      onActivated();
    } catch (e2) { toast.error(errMsg(e2)); setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-5 text-left" data-testid="crm-pay-panel">
      <div className="rounded-2xl border border-border bg-muted/40 p-5 space-y-3">
        <a href={ent.payment_link || "#"} target="_blank" rel="noopener noreferrer" data-testid="crm-pay-link"
          onClick={(e) => { if (!ent.payment_link) { e.preventDefault(); toast.error("Payment link is being configured — please contact support."); } }}
          className="w-full h-12 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}>
          Pay ₹{ent.price_monthly} for CRM <ExternalLink className="h-4 w-4" />
        </a>
        <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
          <li>Open the payment page and pay ₹{ent.price_monthly}.</li>
          <li>Note the UTR / transaction reference number.</li>
          <li>Submit the reference here — verification is manual.</li>
          <li>CRM unlocks right after approval (usually within 24 hours).</li>
        </ol>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input required data-testid="crm-pay-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
          placeholder="UTR / Transaction Reference *" className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm" />
        <input type="date" data-testid="crm-pay-date" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
          className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm" />
        <input data-testid="crm-pay-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="Note (optional — payer name, UPI ID)" className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm" />
        <button type="submit" disabled={busy} data-testid="crm-pay-submit"
          className="w-full h-11 rounded-lg font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-60 bg-emerald-700 hover:bg-emerald-800 transition-colors">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} I Have Completed Payment — Submit
        </button>
      </form>
    </div>
  );
}

function LockScreen({ ent, onActivated }) {
  const pending = ent?.status === "payment_pending";
  const expired = ent?.status === "expired";
  const needsInfo = ent?.pending_payment?.status === "needs_info";
  return (
    <div className="flex items-center justify-center py-10" data-testid="crm-locked">
      <div className="w-full max-w-2xl rounded-3xl overflow-hidden border border-border shadow-2xl">
        <div className="p-8 text-white" style={{ backgroundImage: "linear-gradient(135deg, #065f46, #0f766e)" }}>
          <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center mb-4"><Lock className="h-6 w-6" /></div>
          <h2 className="font-display font-extrabold text-2xl tracking-tight">{expired ? "CRM Subscription Expired" : "CRM is Locked"}</h2>
          <p className="text-emerald-100/90 text-sm mt-1">Advanced CRM is available with a monthly subscription.</p>
        </div>
        <div className="p-8 bg-background space-y-6">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-extrabold text-4xl tracking-tight" data-testid="crm-price">₹{ent.price_monthly}</span>
            <span className="text-sm text-muted-foreground font-semibold">/month</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {CRM_FEATURES.map((f) => (
              <p key={f} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> {f}</p>
            ))}
          </div>
          {expired && <p className="text-xs text-amber-600 font-bold">Your data is safe — leads, deals, forms and reports are never deleted. Renew to continue where you left off.</p>}
          {pending && !needsInfo && (
            <div data-testid="crm-pending-banner" className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0"><Clock className="h-5 w-5" /></div>
              <div>
                <p className="font-bold text-sm">Your CRM payment has been submitted for verification.</p>
                <p className="text-xs text-muted-foreground mt-1">{ent.verification_message}</p>
                {ent.pending_payment?.reference && <p className="text-[11px] text-muted-foreground mt-2">Ref {ent.pending_payment.reference} · ₹{ent.pending_payment.amount}</p>}
              </div>
            </div>
          )}
          {pending && needsInfo && (
            <div className="space-y-4">
              <div data-testid="crm-needsinfo-banner" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-red-500/15 text-red-600 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5" /></div>
                <div>
                  <p className="font-bold text-sm">More information needed</p>
                  <p className="text-xs text-muted-foreground mt-1">{ent.pending_payment?.admin_note || "Please resubmit with the correct payment reference below."}</p>
                </div>
              </div>
              <CrmPayPanel ent={ent} onActivated={onActivated} />
            </div>
          )}
          {!pending && (
            <div className="space-y-3">
              <p className="text-sm font-bold" data-testid="crm-subscribe-label">{expired ? `Renew CRM — ₹${ent.price_monthly}/month` : `Subscribe to CRM — ₹${ent.price_monthly}/month`}</p>
              <CrmPayPanel ent={ent} onActivated={onActivated} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StoreCrm() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "dashboard";
  const [ent, setEnt] = useState(null);
  const [entErr, setEntErr] = useState("");
  const [drawer, setDrawer] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const loadEnt = useCallback(() => {
    api.get("/store-admin/crm/entitlement").then((r) => setEnt(r.data)).catch((e) => setEntErr(errMsg(e)));
  }, []);
  useEffect(() => { loadEnt(); }, [loadEnt]);

  if (entErr) return <p className="text-sm text-red-500" data-testid="crm-ent-error">{entErr}</p>;
  if (!ent) return <div className="h-48 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;
  if (ent.status !== "active") return <LockScreen ent={ent} onActivated={loadEnt} />;

  const goTab = (t) => { setSp({ tab: t }); setDrawer(false); };
  const NavItems = ({ onNav }) => (
    <>
      {SECTIONS.map(({ k, label, icon: Icon }) => (
        <button key={k} onClick={() => (onNav ? onNav(k) : goTab(k))} data-testid={`crm-nav-${k}`}
          className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] font-bold transition-colors ${tab === k ? "bg-emerald-700 text-white shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
          <Icon className="h-4 w-4 shrink-0" /> {label}
        </button>
      ))}
    </>
  );

  return (
    <div className="space-y-5" data-testid="store-crm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setDrawer(true)} data-testid="crm-menu-btn"
            className="lg:hidden h-10 w-10 rounded-xl border border-border flex items-center justify-center"><Menu className="h-4 w-4" /></button>
          <div>
            <h1 className="font-display font-extrabold text-2xl tracking-tight flex items-center gap-2">
              CRM
              {ent.test && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold uppercase tracking-wide">Test</span>}
            </h1>
            <p className="text-xs text-muted-foreground">Leads → Pipeline → Deals → Customers</p>
          </div>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="crm-add-lead"
          className="h-10 px-4 sm:px-5 rounded-full text-white font-bold text-sm flex items-center gap-2 active:scale-95 transition-all"
          style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}>
          <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Add Lead</span><span className="sm:hidden">Lead</span>
        </button>
      </div>

      {ent.renewal_due && (
        <div data-testid="crm-renewal-banner" className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0"><Clock className="h-5 w-5" /></div>
            <div className="flex-1 min-w-52">
              <p className="font-bold text-sm">CRM Renewal Due — ₹{ent.price_monthly}/month</p>
              <p className="text-xs text-muted-foreground">Your subscription expires {ent.expires_at ? new Date(ent.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "soon"}. Renew to keep CRM active without interruption.</p>
            </div>
            <button data-testid="crm-renew-btn" onClick={() => setRenewOpen((o) => !o)}
              className="h-10 px-5 rounded-full text-white font-bold text-sm active:scale-95 transition-all"
              style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}>
              {renewOpen ? "Hide Renewal" : "Renew CRM"}
            </button>
          </div>
          {renewOpen && <CrmPayPanel ent={ent} onActivated={() => { setRenewOpen(false); loadEnt(); }} />}
        </div>
      )}

      <div className="flex gap-6 items-start">
        <aside className="hidden lg:block w-52 shrink-0 sticky top-20 rounded-2xl border border-border bg-background p-2.5 space-y-1" data-testid="crm-sidebar">
          <NavItems />
        </aside>

        {drawer && (
          <div className="fixed inset-0 z-50 lg:hidden" data-testid="crm-drawer">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDrawer(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-background border-r border-border p-3 space-y-1 overflow-y-auto">
              <div className="flex items-center justify-between px-2 py-2 mb-1">
                <span className="font-display font-extrabold">CRM Menu</span>
                <button onClick={() => setDrawer(false)} className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>
              <NavItems onNav={goTab} />
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0">
          {tab === "dashboard" && <CrmDashboard refreshKey={refreshKey} goTab={goTab} />}
          {tab === "leads" && <CrmLeads refreshKey={refreshKey} />}
          {tab === "contacts" && <CrmContacts refreshKey={refreshKey} />}
          {tab === "pipeline" && <CrmPipeline refreshKey={refreshKey} />}
          {tab === "deals" && <CrmDeals refreshKey={refreshKey} />}
          {tab === "followups" && <CrmFollowUps refreshKey={refreshKey} />}
          {tab === "forms" && <CrmForms refreshKey={refreshKey} goTab={goTab} />}
          {tab === "import" && <CrmImport refreshKey={refreshKey} />}
          {tab === "facebook" && <CrmFacebook refreshKey={refreshKey} />}
          {tab === "reports" && <CrmReports />}
          {tab === "notifications" && <CrmNotifications />}
          {tab === "settings" && <CrmSettings ent={ent} onChanged={loadEnt} />}
        </div>
      </div>

      <LeadModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={bump} />
    </div>
  );
}
